import { useLayoutEffect, useRef, useState } from 'react';

import type { ChartSize } from './chart-options';

/**
 * Rounding step for the reported size, in px.
 *
 * Chart options are rebuilt whenever this changes, so reporting every pixel of a
 * drag-resize would rebuild them a hundred times a second. The axis decisions
 * that consume the size are coarse enough that 20px is invisible.
 */
const SIZE_STEP = 20;

const quantise = (value: number): number => Math.round(value / SIZE_STEP) * SIZE_STEP;

/**
 * Size of the element the ref is attached to, quantised and kept current.
 *
 * Measured with `useLayoutEffect` so the first paint already has real numbers —
 * charts decide how to lay their axis labels out from this, and a frame of
 * "unknown size" would show upright labels that immediately tilt.
 *
 * `{ width: 0, height: 0 }` means "not measured yet" (also the case wherever
 * `ResizeObserver` is missing, e.g. jsdom); callers must treat it as unknown
 * rather than as a real zero.
 */
export const useContainerSize = <T extends HTMLElement>() => {
  const ref = useRef<T>(null);
  const [size, setSize] = useState<ChartSize>({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    const measure = () => {
      const next = { width: quantise(element.clientWidth), height: quantise(element.clientHeight) };

      setSize((previous) => (previous.width === next.width && previous.height === next.height ? previous : next));
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return [ref, size] as const;
};
